/**
 * useAuditLogs — TanStack Query hook that reads audit_logs directly via Supabase client.
 * Per US-008 PAGE-007 notes: direct Supabase query, not FastAPI endpoint.
 * RLS scopes rows to the authenticated user's client (job_id IN client's jobs).
 * FR-074, NFR-007.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { AuditLog } from '@/types/index';
import type { AuditFilters } from '@/pages/audit/AuditLogFilters';

const PAGE_SIZE = 50;

export interface AuditLogsResult {
  data: AuditLog[];
  totalCount: number;
}

export interface UseAuditLogsParams {
  filters: AuditFilters;
  page: number;
}

export const auditKeys = {
  all: ['audit'] as const,
  list: (params: UseAuditLogsParams) => ['audit', 'list', params] as const,
};

export function useAuditLogs({ filters, page }: UseAuditLogsParams) {
  return useQuery({
    queryKey: auditKeys.list({ filters, page }),
    queryFn: async (): Promise<AuditLogsResult> => {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let q = supabase
        .from('audit_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      // Event type filter — comma-separated string from multi-select
      if (filters.eventTypes.length === 1) {
        q = q.eq('event_type', filters.eventTypes[0]);
      } else if (filters.eventTypes.length > 1) {
        q = q.in('event_type', filters.eventTypes);
      }

      // Job ID filter — ilike for partial UUID match
      if (filters.jobId.trim()) {
        q = q.ilike('job_id::text', `%${filters.jobId.trim()}%`);
      }

      // Date range
      if (filters.dateFrom) {
        q = q.gte('created_at', `${filters.dateFrom}T00:00:00.000Z`);
      }
      if (filters.dateTo) {
        q = q.lte('created_at', `${filters.dateTo}T23:59:59.999Z`);
      }

      // Free-text search against actor_id cast (basic — full search is backend-side)
      if (filters.search.trim()) {
        q = q.ilike('actor_id::text', `%${filters.search.trim()}%`);
      }

      const { data, error, count } = await q;

      if (error) throw new Error(error.message);

      return {
        data: data as AuditLog[],
        totalCount: count ?? 0,
      };
    },
    placeholderData: (prev) => prev,
  });
}
